import { useSubaccountAssets } from 'src/hooks/assets/useSubaccountAssets';
import { useWalletAssets } from 'src/hooks/assets/useWalletAssets';
import { UIMarket } from '@drift/common';
import { useMappedIfStakeBalances } from 'src/hooks/insuranceFund/useMappedIfStakeBalances';
import { useMemo } from 'react';
import { SpotMarketConfig } from '@drift-labs/sdk';
import { AssetListAsset, AssetRenderingProps } from 'src/@types/assets';

interface Params<T extends AssetListAsset> {
	assetList: T[];
	source: 'wallet' | 'subaccount' | 'ifStake' | 'default';
	overrideCurrentUserKey?: string;
	collateralFilter?: (balance: AssetRenderingProps) => boolean;
	searchValue?: string;
}

/**
 * Returns appropriate collateral options based on `source` and `assetList`.
 * Options are sorted and, if specified, pre-filtered.
 */
export default function useCollateralSelectOptions<T extends AssetListAsset>({
	assetList,
	source,
	overrideCurrentUserKey,
	collateralFilter,
	searchValue,
}: Params<T>) {
	const walletAssets = useWalletAssets(assetList);
	const subaccountAssets = useSubaccountAssets(
		assetList,
		overrideCurrentUserKey
	);
	const mappedIfStakeBalances = useMappedIfStakeBalances();

	return useMemo(() => {
		const optionsToUse = (() => {
			if (source === 'wallet') {
				return walletAssets;
			}

			if (source === 'subaccount') {
				return subaccountAssets;
			}

			if (source === 'ifStake') {
				return UIMarket.spotMarkets
					.map((market) => UIMarket.createSpotMarket(market.marketIndex))
					.map((market) => ({
						...market,
						uiAmount:
							mappedIfStakeBalances.find(
								(balance) => balance.market?.marketIndex === market.marketIndex
							)?.baseValue ?? 0,
						ticker: market.market.symbol,
						displayLabel: market.uiSymbols.marketDisplaySymbol,
						uiAmountString:
							mappedIfStakeBalances
								.find(
									(balance) =>
										balance.market?.marketIndex === market.marketIndex
								)
								?.baseValue?.toString() ?? '0',
						mint: (market.market as SpotMarketConfig).mint.toBase58(),
						value: market.uiSymbols.baseAssetSymbol,
						label: market.uiSymbols.marketDisplaySymbol,
					}));
			}

			// Add `uiAmount` so it matches the other types for convenience.
			return assetList.map((option) => ({
				...option,
				uiAmount: 0,
			}));
		})();

		const sortedOptions = [...optionsToUse].sort((a, b) => {
			if (a.ticker === 'SOL' && a.uiAmount > 0) {
				return -1;
			}

			if (b.ticker === 'SOL' && b.uiAmount > 0) {
				return 1;
			}

			return b.uiAmount - a.uiAmount;
		});

		// Apply "pre filtering" if necessary.
		return sortedOptions.filter((asset) => {
			const preFilter = collateralFilter ? collateralFilter(asset) : true;

			if (preFilter) {
				return searchValue
					? asset.ticker.toLowerCase().includes(searchValue.toLowerCase())
					: true;
			}

			return false;
		});
	}, [
		source,
		walletAssets,
		subaccountAssets,
		searchValue,
		mappedIfStakeBalances,
		!!collateralFilter,
	]);
}
